import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import PreloaderOverlay from "../components/Misc/Preloader/Overlay";
// import { featuresAllowed } from "@static/features";
/**
 * ============================================
 * PAGE DEFINITIONS
 * ============================================
 */

const ClientAPI = lazy(() => import("../pages/ClientAPI"));
const NotFound = lazy(() => import("../pages/404"));
const SignIn = lazy(() => import("../pages/SignIn"));
const ChallengePassword = lazy(() => import("../pages/ChallengePassword"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const SsoCallback = lazy(() => import("../pages/SsoCallback"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Reports = lazy(() => import("../pages/FanInsights/Reports"));
const Report = lazy(() => import("../pages/FanInsights/Report"));
const BuildReport = lazy(() => import("../pages/FanInsights/BuildReport"));
const EditReport = lazy(() => import("../pages/FanInsights/EditReport"));
const Audiences = lazy(() => import("../pages/FanInsights/Audiences"));
const BuildAudience = lazy(() => import("../pages/FanInsights/BuildAudience"));
const EditAudience = lazy(() => import("../pages/FanInsights/EditAudience"));

const BuildMediaReport = lazy(() => import("../pages/Media/BuildMediaReport"));
const EditMediaReport = lazy(() => import("../pages/Media/EditMediaReport"));
const MediaReports = lazy(() => import("../pages/Media/MediaReports"));

// Custom Projects
const CustomReports = lazy(() =>
  import("../pages/CustomProjects/CustomReports")
);
const CustomAudiences = lazy(() =>
  import("../pages/CustomProjects/CustomAudiences")
);
const CustomReport = lazy(() => import("../pages/CustomProjects/CustomReport"));
const CustomEditReport = lazy(() =>
  import("../pages/CustomProjects/CustomEditReport")
);
const CustomBuildReport = lazy(() =>
  import("../pages/CustomProjects/CustomBuildReport")
);
const CustomBuildAudience = lazy(() =>
  import("../pages/CustomProjects/CustomBuildAudience")
);
const CustomEditAudience = lazy(() =>
  import("../pages/CustomProjects/CustomEditAudience")
);

// Fan Rankers
const FanRankers = lazy(() => import("../pages/FanRankers/FanRankers"));

// International Fan Insights
const InternationalReports = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalReports")
);
const InternationalAudiences = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalAudiences")
);
const InternationalReport = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalReport")
);
const InternationalEditReport = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalEditReport")
);
const InternationalBuildReport = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalBuildReport")
);
const InternationalBuildAudience = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalBuildAudience")
);
const InternationalEditAudience = lazy(() =>
  import("../pages/InternationalFanInsights/InternationalEditAudience")
);

// Admin
const AdminPermissions = lazy(() => import("../pages/Admin/AdminPermissions"));
const AdminOrganizations = lazy(() =>
  import("../pages/Admin/AdminOrganizations")
);

const AdminOrganizationDetail = lazy(() =>
  import("../pages/Admin/AdminOrganizationDetail")
);
const AdminMediaSessions = lazy(() =>
  import("../pages/Admin/AdminMediaSessions")
);
const AdminBrandOverride = lazy(() =>
  import("../pages/Admin/AdminBrandOverride")
);
const AdminQuestionMap = lazy(() => import("../pages/Admin/AdminQuestionMap"));

// Misc
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));

const AdminIdPCreate = lazy(() =>
  import("../pages/Admin/AdminIDPCreate")
);

const AdminIdPDetail = lazy(() =>
  import("../pages/Admin/AdminIDPDetail")
);

const AuthenticatedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated)
    return (
      <Navigate to="/signin" replace state={{ path: location.pathname }} />
    );

  return children;
};

const PublicRoute = ({ children, isAPIDocs = false }) => {
  const { isAuthenticated } = useAuth();
  const { state } = useLocation();

  if (isAuthenticated && !isAPIDocs)
    return <Navigate to={state?.path ? state.path : "/"} replace />;

  return children;
};

const FeatureRoute = ({ children, featureId }) => {
  const { featurePermissions, isAdmin, isMediaAdmin, metaData } = useAuth();
  const { state, pathname } = useLocation();

  try {
    let {
      is_media_enabled,
      is_faninsights_enabled,
      is_custom_projects_enabled,
      is_international_fan_insights_enabled,
    } = featurePermissions;

    switch (featureId) {
      case "media":
        if (
          is_media_enabled !== true &&
          isAdmin !== true &&
          isMediaAdmin !== true
        ) {
          throw new Error("Feature not accessible by user.");
        }

        break;
      case "faninsights":
        if (is_faninsights_enabled !== true) {
          throw new Error("Feature not accessible by user.");
        }
        break;
      case "fan_rankers":
        if (
          // isAdmin !== true &&
          is_faninsights_enabled !== true ||
          metaData?.features?.Rankers !== true
        ) {
          throw new Error("Feature not accessible by user.");
        }

        break;
      case "custom_projects":
        if (is_custom_projects_enabled !== true && isAdmin !== true) {
          throw new Error("Feature not accessible by user.");
        }

        break;
      case "international_fan_insights":
        if (
          is_international_fan_insights_enabled !== true &&
          isAdmin !== true
        ) {
          throw new Error("Feature not accessible by user.");
        }

        break;
      default:
        break;
    }

    return children;
  } catch (error) {
    console.log(error);
    return (
      <Navigate
        to={state?.path ? state.path : "/"}
        replace
        state={{ path: pathname }}
      />
    );
  }
};

const UserAdministratorRoute = ({ children }) => {
  const { isAdmin, isUserAdmin } = useAuth();
  const { state, pathname } = useLocation();

  try {
    if (isAdmin || isUserAdmin) {
      return children;
    } else {
      throw new Error("Feature not accessible by user");
    }
  } catch (error) {
    console.log(error);

    return (
      <Navigate
        to={state?.path ? state.path : "/"}
        replace
        state={{ path: pathname }}
      />
    );
  }
};

const AdministratorRoute = ({ children }) => {
  const { isAdmin } = useAuth();
  const { state, pathname } = useLocation();

  return isAdmin ? (
    children
  ) : (
    <Navigate
      to={state?.path ? state.path : "/"}
      replace
      state={{ path: pathname }}
    />
  );
};

const MediaAdministratorRoute = ({ children }) => {
  const { isAdmin, isMediaAdmin } = useAuth();
  const { state, pathname } = useLocation();

  try {
    if (isAdmin || isMediaAdmin) {
      return children;
    } else {
      throw new Error("Feature not accessible by user");
    }
  } catch (error) {
    console.log(error);

    return (
      <Navigate
        to={state?.path ? state.path : "/"}
        replace
        state={{ path: pathname }}
      />
    );
  }
};

export default function Router() {
  const { user, isAuthenticated, featurePermissions } = useAuth();
  const { state, pathname } = useLocation();
  return (
    <Suspense
      fallback={
        <main className="page page-app">
          <div className="page-builder-wrap" style={{ position: "relative" }}>
            <PreloaderOverlay
              active={true}
              isAbsolute={true}
              message={"Loading..."}
            />
          </div>
        </main>
      }
    >
      <Routes>
        <Route
          path="/signin"
          element={
            <PublicRoute>
              <SignIn />
            </PublicRoute>
          }
        />
        <Route
          path="/newpassword"
          element={
            <PublicRoute>
              <ChallengePassword />
            </PublicRoute>
          }
        />
        <Route
          path="/recovery"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/sso/callback"
          element={
            <PublicRoute>
              <SsoCallback />
            </PublicRoute>
          }
        />
        <Route
          exact
          path="/"
          element={
            <AuthenticatedRoute>
              <Dashboard />
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/reports"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="faninsights">
                  <Reports />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/reports/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="faninsights">
                  <Report />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/reports/edit/:id"
          element={
            user &&
            featurePermissions && (
              <AuthenticatedRoute>
                <FeatureRoute featureId="faninsights">
                  <EditReport />
                </FeatureRoute>
              </AuthenticatedRoute>
            )
          }
        />
        <Route
          exact
          path="/reports/new"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="faninsights">
                  <BuildReport />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/audiences"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="faninsights">
                  <Audiences />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/audiences/edit/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="faninsights">
                  <EditAudience />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/audiences/new"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="faninsights">
                  <BuildAudience />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        {/* Fan Rankers Routes */}
        <Route
          exact
          path="/fan-rankers"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="fan_rankers">
                  <FanRankers />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />

        {/* New Media Routes */}
        <Route
          exact
          path="/mediareports"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="media">
                  <MediaReports />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />

        <Route
          exact
          path="/media/edit/:id"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="media">
                  <EditMediaReport />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />

        {/* New Custom Projects Routes */}
        <Route
          exact
          path="/custom-projects/:projectId/reports"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="custom_projects">
                  <CustomReports />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />

        <Route
          exact
          path="/custom-projects/:projectId/reports/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="custom_projects">
                  <CustomReport />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/custom-projects/:projectId/reports/edit/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="custom_projects">
                  <CustomEditReport />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        {/* // new  */}
        <Route
          exact
          path="/custom-projects/:projectId/reports/new"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="custom_projects">
                  <CustomBuildReport />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/custom-projects/:projectId/audiences"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="custom_projects">
                  <CustomAudiences />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/custom-projects/:projectId/audiences/new"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="custom_projects">
                  <CustomBuildAudience />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/custom-projects/:projectId/audiences/edit/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="custom_projects">
                  <CustomEditAudience />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />

        {/* End New Custom Projects Routes */}

        {/* New International Fan Insights Projects Routes */}
        <Route
          exact
          path="/international/reports"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalReports />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />

        <Route
          exact
          path="/international/reports/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalReport />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        <Route
          exact
          path="/international/reports/edit/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalEditReport />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />
        {/* // new  */}
        <Route
          exact
          path="/international/reports/new"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalBuildReport />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/international/audiences"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalAudiences />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/international/audiences/new"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalBuildAudience />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/international/audiences/edit/:id"
          element={
            <AuthenticatedRoute>
              {user && featurePermissions && (
                <FeatureRoute featureId="international_fan_insights">
                  <InternationalEditAudience />
                </FeatureRoute>
              )}
            </AuthenticatedRoute>
          }
        />

        {/* End New International Fan Insights Routes */}

        <Route
          exact
          path="/admin/permissions/:id"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <MediaAdministratorRoute>
                  <AdminPermissions />
                </MediaAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/admin/question-map"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <AdministratorRoute>
                  <AdminQuestionMap />
                </AdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <Navigate
                to={isAuthenticated ? "/" : "/signin"}
                replace
                state={{ path: pathname }}
              />
            )
          }
        />
        <Route
          exact
          path="/admin/organizations"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <UserAdministratorRoute>
                  <AdminOrganizations />
                </UserAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/admin/organizations/:id"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <UserAdministratorRoute>
                  <AdminOrganizationDetail />
                </UserAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />

        <Route
          exact
          path="/admin/media-sessions"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <MediaAdministratorRoute>
                  <AdminMediaSessions />
                </MediaAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/client-api"
          element={
            // user && user?.attributes ? (
            <PublicRoute isAPIDocs={true}>
              <ClientAPI />
            </PublicRoute>
            // ) : (
            //   <>
            //     {isAuthenticated ? (
            //       <main className="page page-app">
            //         <div
            //           className="page-builder-wrap"
            //           style={{ position: "relative" }}
            //         >
            //           <PreloaderOverlay
            //             active={true}
            //             isAbsolute={true}
            //             message={"Loading..."}
            //           />
            //         </div>
            //       </main>
            //     ) : (
            //       <Navigate
            //         to={state?.path ? state.path : "/signin"}
            //         replace
            //         state={{ path: pathname }}
            //       />
            //     )}
            //   </>
            // )
          }
        ></Route>
        <Route
          exact
          path="/admin/brand-override"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <MediaAdministratorRoute>
                  <AdminBrandOverride />
                </MediaAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/admin/organizations/:id/identity-providers/create"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <UserAdministratorRoute>
                  <AdminIdPCreate />
                </UserAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        <Route
          exact
          path="/admin/organizations/:id/identity-providers/:providerId"
          element={
            user && user?.attributes ? (
              <AuthenticatedRoute>
                <UserAdministratorRoute>
                  <AdminIdPDetail />
                </UserAdministratorRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />

        <Route
          exact
          path="/media/new"
          element={
            user && user?.attributes && featurePermissions ? (
              <AuthenticatedRoute>
                <FeatureRoute featureId="media">
                  <BuildMediaReport />
                </FeatureRoute>
              </AuthenticatedRoute>
            ) : (
              <>
                {isAuthenticated ? (
                  <main className="page page-app">
                    <div
                      className="page-builder-wrap"
                      style={{ position: "relative" }}
                    >
                      <PreloaderOverlay
                        active={true}
                        isAbsolute={true}
                        message={"Loading..."}
                      />
                    </div>
                  </main>
                ) : (
                  <Navigate
                    to={state?.path ? state.path : "/signin"}
                    replace
                    state={{ path: pathname }}
                  />
                )}
              </>
            )
          }
        />
        {/* End Media Routes */}
        <Route
          path="/privacy-policy"
          element={
            <AuthenticatedRoute>
              <PrivacyPolicy />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <NotFound />
            ) : (
              <Navigate
                to={state?.path ? state.path : "/signin"}
                replace
                state={{ path: pathname }}
              />
            )
          }
        />
      </Routes>
    </Suspense>
  );
}
